// AuthContext.js - Improved with better structure and comments
import React, { createContext, useState, useEffect, useContext } from 'react';
import apiClient from '../api/client';

// Section 2: Context creation
export const AuthContext = createContext();

// Section 3: AuthProvider component - manages authentication state
export const AuthProvider = ({ children }) => {
  // State initialization
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);

  // Section 5: Authentication check on app load
  useEffect(() => {
    const checkAuth = async () => {
      const storedToken = localStorage.getItem('token');
      const storedUser = localStorage.getItem('user');
      
      if (storedToken && storedUser) {
        try {
          const response = await apiClient.get(`/auth/verify?t=${Date.now()}`);
          
          if (response.data?.user) {
            setUser(response.data.user);
            setToken(storedToken);
            setIsAuthenticated(true);
          }
        } catch (error) {
          if (error.response?.status === 401) {
            logout();
          }
        }
      }
      setLoading(false);
    };

    checkAuth();
  }, []);

  // Section 6: Login function
  const login = async (username, password) => {
    try {
      const response = await apiClient.post('/auth/login', {
        username,
        password
      });
    
      const { access_token, user: userData } = response.data;
      
      // Store authentication data
      localStorage.setItem('token', access_token);
      localStorage.setItem('user', JSON.stringify(userData));
      
      // Update state
      setToken(access_token);
      setUser(userData);
      setIsAuthenticated(true);
      
      return { success: true, user: userData };
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Login failed. Please check credentials.';
      return { success: false, error: errorMessage };
    }
  };

  // Section 7: Logout function
  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    setToken(null);
    setIsAuthenticated(false);
  };

  // Section 8: Permission checking function
  const hasPermission = (permission) => {
    if (!user) return false;
    if (user.role === 'superadmin') return true;
    return user.permissions && user.permissions.includes(permission);
  };

  // Section 9: Context value
  const value = {
    user,
    token,
    isAuthenticated,
    loading,
    login,
    logout,
    hasPermission,
    isAdmin: user?.role === 'admin' || user?.role === 'superadmin'
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Section 10: Custom hook for using auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};